import { createContext, Dispatch, PropsWithChildren, useContext, useReducer } from 'react'

import { DefaultElementProps } from 'common/interfaces'

import { MapSelectActionsReturn } from './actions'
import { MapSelect as MapSelectProps, MapSelectPoint } from './interface'
import mapSelectReducer from './reducer'

type MapSelectStateContextI = Pick<DefaultElementProps, 'loading' | 'onInteraction'> &
  Pick<MapSelectProps, 'pointButtonText'> & {
    addressList: MapSelectPoint[]
    isSlideOpen: boolean
    selectedMarker?: SelectedMarkerI
    isSidebarOpen: boolean
    isZoomAlertMessageOpen: boolean
  }

export interface SelectedMarkerI {
  address: string
  name: string
}

const mapSelectDefaultStateContext: MapSelectStateContextI = {
  addressList: [],
  isSlideOpen: false,
  selectedMarker: undefined,
  isSidebarOpen: true,
  isZoomAlertMessageOpen: false,
}

type DispatchMapSelectActions = Dispatch<MapSelectActionsReturn>

const MapSelectContext = createContext<MapSelectStateContextI | undefined>(undefined)
const MapSelectDispatchContext = createContext<DispatchMapSelectActions | undefined>(undefined)

function useMapSelectState(): MapSelectStateContextI {
  const context = useContext(MapSelectContext)
  if (context === undefined) {
    throw new Error('useMapSelectState must be used within a MapSelectProvider')
  }

  return context
}

function useMapSelectDispatch(): DispatchMapSelectActions {
  const context = useContext(MapSelectDispatchContext)
  if (context === undefined) {
    throw new Error('useMapSelectDispatch must be used within a MapSelectProvider')
  }

  return context
}

type MapSelectProviderProps = PropsWithChildren<
  Pick<DefaultElementProps, 'loading' | 'onInteraction'> & Pick<MapSelectProps, 'pointButtonText'>
>

function MapSelectProvider({
  children,
  loading,
  onInteraction,
  pointButtonText,
}: MapSelectProviderProps): JSX.Element {
  const [state, dispatch] = useReducer(mapSelectReducer, mapSelectDefaultStateContext)

  return (
    <MapSelectContext.Provider value={{ ...state, loading, onInteraction, pointButtonText }}>
      <MapSelectDispatchContext.Provider value={dispatch}>
        {children}
      </MapSelectDispatchContext.Provider>
    </MapSelectContext.Provider>
  )
}

export { MapSelectProvider, useMapSelectState, useMapSelectDispatch, MapSelectStateContextI }
